import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { HeroMedia } from '../components/HeroMedia';
import { useLanguageStore, useLeadCaptureStore, useAppearanceStore } from '../store';
import { useTranslation } from '../i18n/translations';

// Резервные базовые изображения баннера
const DEFAULT_HERO_IMAGES = [
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80',
];

/**
 * Универсальный оптимизатор изображений.
 * Сжимает вес картинок Cloudinary и Unsplash на лету до 50-80 КБ для молниеносного рендеринга.
 */
const getOptimizedUrl = (url, width = 1200, quality = 75, blur = false) => {
  if (!url || typeof url !== 'string') return url;

  // 1. Оптимизация Cloudinary
  if (url.includes('res.cloudinary.com')) {
    const uploadIndex = url.indexOf('/upload/');
    if (uploadIndex === -1) return url;
    let transformations = `f_auto,q_${quality}`;
    if (width) transformations += `,w_${width}`;
    if (blur) transformations += `,e_blur:1000,w_40`;
    return `${url.slice(0, uploadIndex)}/upload/${transformations}${url.slice(uploadIndex + 7)}`;
  }

  // 2. Оптимизация Unsplash
  if (url.includes('images.unsplash.com')) {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('auto', 'format');
      urlObj.searchParams.set('fit', 'crop');
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('q', blur ? '10' : quality.toString());
      if (blur) {
        urlObj.searchParams.set('blur', '100');
      }
      return urlObj.toString();
    } catch (e) {
      return url;
    }
  }

  return url;
};

/**
 * Компонент прогрессивной загрузки изображений.
 * Резервирует aspect-ratio для исключения сдвигов макета (CLS).
 */
const LocalOptimizedImage = ({ src, alt, className = '', containerClassName = '', aspect = 'aspect-square' }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [highResSrc, setHighResSrc] = useState('');

  const blurredPlaceholder = getOptimizedUrl(src, 40, 10, true);
  const optimizedSrc = getOptimizedUrl(src, 800, 75, false);

  useEffect(() => {
    setIsLoaded(false);
    if (src) {
      const img = new Image();
      img.src = optimizedSrc;
      img.onload = () => {
        setHighResSrc(optimizedSrc);
        setIsLoaded(true);
      };
    }
  }, [src, optimizedSrc]);

  return (
    <div className={`relative overflow-hidden bg-neutral-100 ${aspect} ${containerClassName}`}>
      <img
        src={blurredPlaceholder}
        alt={alt}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${className}`}
        style={{ filter: 'blur(8px)', transform: 'scale(1.05)', opacity: isLoaded ? 0 : 1 }}
      />
      {highResSrc && (
        <img
          src={highResSrc}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
        />
      )}
    </div>
  );
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } },
};

/* STREAMING_CHUNK: Initializing state hooks for optimized home controller... */
export const HomePage = () => {
  const { language } = useLanguageStore();
  const openLead = useLeadCaptureStore((s) => s.open);
  const { t } = useTranslation(language);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [collectionImages, setCollectionImages] = useState({});
  const [dynamicCollections, setDynamicCollections] = useState([]);

  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const heroCfg = useAppearanceStore((s) => s.config?.hero || {});
  const loaded = useAppearanceStore((s) => s.loaded);
  const fetchAppearance = useAppearanceStore((s) => s.fetchAppearance);

  useEffect(() => {
    fetchAppearance();

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/collection-images`)
      .then((r) => r.json())
      .then((data) => setCollectionImages(data?.images || {}))
      .catch(() => {});

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/collections?hero=true`)
      .then((r) => r.json())
      .then((data) => setDynamicCollections(data?.collections || []))
      .catch(() => {});
  }, [fetchAppearance]);

  // Оптимизированный предрасчет фоновых слайдов главного экрана
  const heroImages = useMemo(() => {
    if (heroCfg?.slides && heroCfg.slides.length > 0) {
      return heroCfg.slides.map((s) => s?.url).filter(Boolean).map(url => getOptimizedUrl(url, 1920, 75));
    }
    return DEFAULT_HERO_IMAGES.map(url => getOptimizedUrl(url, 1920, 75));
  }, [heroCfg?.slides]); 

  // ИСПРАВЛЕНО: Интегрирован ультра-легкий размытый подкладочный фон (1 КБ) для нулевой задержки на первом кадре
  const firstSlideBlurred = useMemo(() => {
    if (heroImages && heroImages[0]) {
      return getOptimizedUrl(heroImages[0], 80, 10, true);
    }
    return '';
  }, [heroImages]);

  const heroInterval = heroCfg?.interval_ms || 6000;

  const heroVideo = useMemo(() => {
    const url = heroCfg?.video_url || heroCfg?.videoUrl;
    if (!url) return null;
    const optimize = (u) => getOptimizedUrl(u, 1200, 75);
    return {
      videoUrl: url,
      webmUrl: heroCfg?.video_webm_url || heroCfg?.videoWebmUrl || '',
      poster: optimize(heroCfg?.poster_url || heroImages[0] || ''),
      mobileImage: optimize(heroCfg?.mobile_image || heroCfg?.poster_url || heroImages[0] || ''),
    };
  }, [heroCfg, heroImages]);

  useEffect(() => {
    if (heroImages.length <= 1) {
      setCurrentHeroIndex(0);
      return undefined;
    }
    
    setCurrentHeroIndex((prev) => (prev >= heroImages.length ? 0 : prev));

    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, heroInterval);
    
    return () => clearInterval(interval);
  }, [heroImages.length, heroInterval]);

  /* STREAMING_CHUNK: Rendering complete validated home page layout... */
  return (
    <div data-testid="home-page">
      <SEO />
      
      {/* ИСПРАВЛЕНО: Подкладочный inline-стиль фона рендерится синхронно, убирая резкий черный экран во время скачивания тяжелого фото */}
      <section 
        className="relative h-screen overflow-hidden" 
        data-testid="hero-section"
        style={{
          backgroundColor: '#FFFFFF',
          backgroundImage: firstSlideBlurred ? `url(${firstSlideBlurred})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'background-image 0.5s ease-in-out'
        }}
      >
        {/* ИСПРАВЛЕНО: HeroMedia монтируется ТОЛЬКО при наличии видео, полностью исключая чёрную вспышку на первом кадре монтирования */}
        {heroVideo ? (
          <HeroMedia
            videoUrl={heroVideo?.videoUrl || ''}
            webmUrl={heroVideo?.webmUrl || ''}
            poster={heroVideo?.poster || ''}
            mobileImage={heroVideo?.mobileImage || ''}
            overlayClassName="bg-black/30"
            data-testid="hero-video"
          />
        ) : (
          <>
            {heroImages.map((img, index) => (
              <motion.div
                key={img}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentHeroIndex ? 1 : 0 }}
                transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <img
                  src={img}
                  alt="FEDYK Furniture Interior"
                  className="w-full h-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"} // Eager load the first image for maximum rendering speed!
                />
              </motion.div>
            ))}
            <div className="absolute inset-0 bg-black/30 z-10" />
          </>
        )}

        <div className="relative z-20 container-custom h-full flex flex-col justify-center pt-24 pb-40">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p variants={fadeUp} className="text-caption text-white/70 mb-6">
              FEDYK Furniture
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-display text-white mb-8 tracking-[0.02em]">
              {t?.hero?.title}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-body-lg text-white/80 mb-10 max-w-xl font-light">
              {t?.hero?.subtitle}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Button
                onClick={() => openLead('hero')}
                className="btn-primary bg-white text-black hover:bg-white/90"
                data-testid="hero-cta"
              >
                {t?.hero?.cta}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </motion.div>

          {!heroVideo && heroImages.length > 1 && (
            <div className="absolute bottom-24 right-6 md:right-24 flex gap-2 z-20">
              {heroImages.map((url, index) => (
                <button
                  key={`indicator-${index}`}
                  onClick={() => setCurrentHeroIndex(index)}
                  className="w-12 h-1 transition-luxe"
                  style={{
                    backgroundColor: index === currentHeroIndex ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.3)'
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Collections Section */}
      <section className="section-padding bg-background" data-testid="categories-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20 md:mb-28">
            <div>
              <p className="text-caption text-muted-foreground mb-4">{t?.nav?.products}</p>
              <h2 className="text-h1">
                {t?.common?.ourCollections || 'Our Collections'}
              </h2>
            </div>
            <Link
              to={`/${language}/products`}
              className="btn-ghost mt-6 md:mt-0"
              data-testid="view-all-products"
            >
              {t?.common?.viewAll}
              <ChevronRight className="ml-1 w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 lg:gap-20">
            {(() => {
              const FALLBACK_IMG = {
                wardrobes: 'https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=1200&q=85&auto=format&fit=crop',
                doors: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=85&auto=format&fit=crop',
                kitchens: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=85&auto=format&fit=crop',
              };
              const FALLBACK_LIST = [
                { id: 'wardrobes', slug: 'wardrobes', name: { en: 'Wardrobes' }, subtitle: 'AIR LINE System' },
                { id: 'doors', slug: 'doors', name: { en: 'Partitions & Doors' }, subtitle: 'INTEGRA & OTIUM' },
                { id: 'kitchens', slug: 'kitchens', name: { en: 'Kitchens' }, subtitle: 'RADIAL' },
              ];
              const list = (dynamicCollections && dynamicCollections.length > 0) ? dynamicCollections : FALLBACK_LIST;
                
              return list.slice(0, 6).map((collection, index) => {
                if (!collection) return null;
                const title = (collection.name && (collection.name[language] || collection.name.en)) || collection.slug || 'Collection';
                
                const databaseImgUrl = collection.image_url || collection.image || collection.imageUrl || collection.cover_url;
                
                const heroImg = databaseImgUrl
                  ? databaseImgUrl
                  : (collectionImages[collection.slug]?.secure_url
                    ? collectionImages[collection.slug].secure_url
                    : (FALLBACK_IMG[collection.slug] || FALLBACK_IMG.wardrobes));
                
                const linkCategory = collection.category || collection.slug;
                return (
                  <motion.div
                    key={collection.id || collection.slug || `col-key-${index}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={`/${language}/products?category=${linkCategory}`}
                      className="group block"
                      data-testid={`collection-${collection.slug}`}
                    >
                      <div className="aspect-[4/5] overflow-hidden bg-muted mb-8">
                        <LocalOptimizedImage
                          src={heroImg}
                          alt={title}
                          aspect="aspect-[4/5]"
                          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                        />
                      </div>
                      <p className="text-caption text-muted-foreground mb-3">
                        {(typeof collection.subtitle === 'object' ? (collection.subtitle?.[language] || collection.subtitle?.en) : collection.subtitle) || ''}
                      </p>
                      <h3 className="collection-title text-2xl md:text-3xl font-extralight group-hover:text-muted-foreground transition-colors duration-500">
                        {title}
                      </h3>
                    </Link>
                  </motion.div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-foreground text-background" data-testid="about-preview">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-caption text-background/50 mb-4">{t?.about?.title}</p>
              <h2 className="text-h1 mb-6">{t?.about?.subtitle}</h2>
              <p className="text-body-lg text-background/70 mb-8">{t?.about?.description}</p>
              <Link to={`/${language}/about`}>
                <Button variant="outline" className="btn-secondary border-background text-background hover:bg-background hover:text-foreground" data-testid="about-cta">
                  {t?.common?.learnMore}
                  <ArrowUpRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <LocalOptimizedImage
                  src={siteImages['home_about_preview']?.url || 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80'}
                  alt="FEDYK Furniture Showroom"
                  aspect="aspect-[4/5]"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* B2B Banner */}
      <section className="section-padding bg-surface" data-testid="b2b-banner">
        <div className="container-custom">
          <div className="bg-foreground text-background p-12 md:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-caption text-background/50 mb-4">{t?.b2b?.title}</p>
                <h2 className="text-h1 mb-6">{t?.b2b?.subtitle}</h2>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="p-4 border border-background/20">
                    <p className="font-medium">{t?.b2b?.architects}</p>
                  </div>
                  <div className="p-4 border border-border/20">
                    <p className="font-medium">{t?.b2b?.designers}</p>
                  </div>
                  <div className="p-4 border border-background/20">
                    <p className="font-medium">{t?.b2b?.developers}</p>
                  </div>
                  <div className="p-4 border border-background/20">
                    <p className="font-medium">{t?.b2b?.dealers}</p>
                  </div>
                </div>
                
                <Button
                  onClick={() => openLead('b2b')}
                  className="btn-primary bg-background text-foreground"
                  data-testid="b2b-cta"
                >
                  {t?.b2b?.cta || 'Join Trade Program / Contact Us'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>

              <div className="hidden lg:block">
                <ul className="space-y-4">
                  <li className="flex items-center gap-4 text-background/80">
                    <span className="w-8 h-8 border border-background/40 flex items-center justify-center text-sm">01</span>
                    {t?.b2b?.tradePricing}
                  </li>
                  <li className="flex items-center gap-4 text-background/80">
                    <span className="w-8 h-8 border border-background/40 flex items-center justify-center text-sm">02</span>
                    {t?.b2b?.downloadCenter}
                  </li>
                  <li className="flex items-center gap-4 text-background/80">
                    <span className="w-8 h-8 border border-background/40 flex items-center justify-center text-sm">03</span>
                    {t?.b2b?.projectSupport}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
